import React, { useEffect, useRef, useState } from "react";
import { useAccessibility } from "./AccessibilityContext";
import "./accessibilityPanel.css";

export default function AccessibilityControls() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const {
    fontScale,
    setFontScale,
    contrast,
    setContrast,
    brightness,
    setBrightness,
    resetAccessibility,
  } = useAccessibility();

  // ESC key + focus trap
  useEffect(() => {
    if (!open) return;

    const focusable = panelRef.current.querySelectorAll(
      "button, input, [tabindex]:not([tabindex='-1'])"
    );

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current.focus();
      }

      if (e.key === "Tab") {
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  return (
    <>
      {/* Floating Button */}
      <button
        ref={buttonRef}
        className="a11y-fab"
        aria-label="Open accessibility settings"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        ♿
      </button>

      {/* Panel */}
      {open && (
        <div
          ref={panelRef}
          className="a11y-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Accessibility settings"
        >
          <div className="a11y-header">
            <span>Accessibility</span>
            <button
              aria-label="Close accessibility panel"
              onClick={() => {
                setOpen(false);
                buttonRef.current.focus();
              }}
            >
              ✕
            </button>
          </div>

          {/* Text Size */}
          <div className="a11y-section">
            <label>Text Size</label>
            <div className="a11y-buttons">
              <button onClick={() => setFontScale(Math.max(1, fontScale - 0.25))}>
                A-
              </button>
              <button onClick={() => setFontScale(Math.min(4, fontScale + 0.25))}>
                A+
              </button>
            </div>
          </div>

          {/* Contrast Slider */}
          <div className="a11y-section">
            <label htmlFor="contrast">Contrast</label>
            <input
              id="contrast"
              type="range"
              min="50"
              max="200"
              step="5"
              value={contrast}
              onChange={(e) => setContrast(Number(e.target.value))}
            />
            <span>{contrast}%</span>
          </div>

          {/* Brightness Slider */}
          <div className="a11y-section">
            <label htmlFor="brightness">Brightness</label>
            <input
              id="brightness"
              type="range"
              min="0.8"
              max="1.5"
              step="0.1"
              value={brightness}
              onChange={(e) => setBrightness(Number(e.target.value))}
            />
            <span>{Math.round(brightness * 100)}%</span>
          </div>

          {/* Reset Button */}
          <div className="a11y-section">
            <button className="full-btn reset-btn" onClick={resetAccessibility}>
              Reset Accessibility
            </button>
          </div>
        </div>
      )}
    </>
  );
}
